/**
 * Store de persistance des sélections de soumissions.
 *
 * Ce store persiste les sélections BUILDING/LANDSCAPE par parcelle
 * dans sessionStorage pour les restaurer après un remount du viewer.
 *
 * IMPORTANT: Ce store ne doit JAMAIS être utilisé pour modifier les
 * visualisations Cesium directement - il sert uniquement à restaurer
 * l'état de sélection après un rechargement du ProjectManager.
 */

import { create } from "zustand";
import type { SubmissionType } from "@/types/project";

/**
 * Clé de stockage dans sessionStorage.
 */
const STORAGE_KEY = "pixatwin:submission-selections";

/**
 * Structure de persistance: { [projectId]: { [parcelId]: { [type]: submissionIds } } }
 */
interface PersistedSelections {
  [projectId: string]: {
    [parcelId: string]: {
      [K in SubmissionType]?: string[];
    };
  };
}

/**
 * État de la sélection active pour restauration.
 */
interface ActiveSelection {
  projectId: string;
  parcelId: string;
  submissionId: string;
  submissionType: SubmissionType;
}

interface SelectionPersistenceState {
  /**
   * Sélection actuellement active (en mémoire) pour restauration rapide.
   */
  activeSelection: ActiveSelection | null;

  /**
   * Toutes les sélections persistées (chargées depuis sessionStorage).
   */
  persistedSelections: PersistedSelections;
}

/**
 * Actions disponibles sur le store.
 */
interface SelectionPersistenceActions {
  /**
   * Charge les sélections depuis sessionStorage.
   */
  loadFromStorage: () => void;

  /**
   * Sauvegarde les sélections courantes dans sessionStorage.
   */
  saveToStorage: () => void;

  /**
   * Enregistre la sélection active (appelé quand l'utilisateur sélectionne une soumission).
   */
  setActiveSelection: (
    projectId: string,
    parcelId: string,
    submissionId: string,
    submissionType: SubmissionType,
    append?: boolean,
  ) => void;

  /**
   * Efface la sélection active.
   */
  clearActiveSelection: () => void;

  /**
   * Récupère la sélection sauvegardée pour une parcelle et un type donné.
   * @returns L'ID de la soumission sélectionnée, ou null si aucune sélection.
   */
  getSelection: (
    projectId: string,
    parcelId: string,
    type: SubmissionType,
  ) => string | null;

  /**
   * Supprime une sélection précise pour une parcelle et un type donnés.
   */
  clearSelection: (
    projectId: string,
    parcelId: string,
    submissionType: SubmissionType,
    submissionId?: string,
  ) => void;

  /**
   * Supprime toutes les sélections pour un projet (appelé quand le projet est déchargé).
   */
  clearProjectSelections: (projectId: string) => void;

  /**
   * Supprime les anciennes sélections invalides (soumissions qui n'existent plus).
   * Appelé lors du chargement d'un projet avec des données fraîches.
   */
  pruneInvalidSelections: (
    projectId: string,
    validSubmissionIds: Set<string>,
  ) => void;
}

type SelectionPersistenceStore = SelectionPersistenceState &
  SelectionPersistenceActions;

/**
 * Helper pour lire le storage.
 */
const readStorage = (): PersistedSelections => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const result: PersistedSelections = {};
    for (const [projectId, parcels] of Object.entries(parsed)) {
      if (!parcels || typeof parcels !== "object" || Array.isArray(parcels)) continue;
      result[projectId] = {};
      for (const [parcelId, types] of Object.entries(parcels)) {
        if (!types || typeof types !== "object" || Array.isArray(types)) continue;
        for (const type of ["BUILDING", "LANDSCAPE"] as const) {
          const value = (types as Record<string, unknown>)[type];
          const ids = typeof value === "string" ? [value] : Array.isArray(value)
            ? value.filter((id): id is string => typeof id === "string" && id.length > 0) : [];
          if (ids.length) {
            result[projectId] ??= {};
            result[projectId][parcelId] ??= {};
            result[projectId][parcelId][type] = [...new Set(ids)];
          }
        }
      }
    }
    return result;
  } catch {
    return {};
  }
};

/**
 * Helper pour écrire dans le storage.
 */
const writeStorage = (data: PersistedSelections): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn(
      "[SelectionPersistenceStore] Impossible de sauvegarder dans sessionStorage:",
      error,
    );
  }
};

/**
 * Store Zustand pour la persistance des sélections.
 */
export const useSelectionPersistenceStore = create<SelectionPersistenceStore>(
  (set, get) => ({
    activeSelection: null,
    persistedSelections: {},

    loadFromStorage: () => {
      const data = readStorage();
      set({ persistedSelections: data });
    },

    saveToStorage: () => {
      const { persistedSelections } = get();
      writeStorage(persistedSelections);
    },

    setActiveSelection: (
      projectId,
      parcelId,
      submissionId,
      submissionType,
      append = false,
    ) => {
      const { persistedSelections } = get();

      // Mettre à jour la structure persistée
      const updatedSelections: PersistedSelections = {
        ...persistedSelections,
        [projectId]: {
          ...(persistedSelections[projectId] ?? {}),
          [parcelId]: {
            ...(persistedSelections[projectId]?.[parcelId] ?? {}),
            [submissionType]: append
              ? [...new Set([...(persistedSelections[projectId]?.[parcelId]?.[submissionType] ?? []), submissionId])]
              : [submissionId],
          },
        },
      };

      // Sauvegarder immédiatement
      writeStorage(updatedSelections);

      set({
        persistedSelections: updatedSelections,
        activeSelection: {
          projectId,
          parcelId,
          submissionId,
          submissionType,
        },
      });
    },

    clearActiveSelection: () => {
      set({ activeSelection: null });
    },

    getSelection: (projectId, parcelId, type) => {
      const { persistedSelections } = get();
      return persistedSelections[projectId]?.[parcelId]?.[type]?.slice(-1)[0] ?? null;
    },

    clearSelection: (projectId, parcelId, submissionType, submissionId) => {
      const { persistedSelections, activeSelection } = get();
      const projectSelections = persistedSelections[projectId];
      const parcelSelections = projectSelections?.[parcelId];

      if (!projectSelections || !parcelSelections?.[submissionType]) {
        return;
      }

      const nextParcelSelections = { ...parcelSelections };
      const remaining = submissionId
        ? (nextParcelSelections[submissionType] ?? []).filter((id) => id !== submissionId)
        : [];
      if (remaining.length) nextParcelSelections[submissionType] = remaining;
      else delete nextParcelSelections[submissionType];

      const nextProjectSelections = { ...projectSelections };
      if (Object.keys(nextParcelSelections).length === 0) {
        delete nextProjectSelections[parcelId];
      } else {
        nextProjectSelections[parcelId] = nextParcelSelections;
      }

      const updatedSelections = { ...persistedSelections };
      if (Object.keys(nextProjectSelections).length === 0) {
        updatedSelections[projectId] = {};
      } else {
        updatedSelections[projectId] = nextProjectSelections;
      }

      writeStorage(updatedSelections);
      set({
        persistedSelections: updatedSelections,
        activeSelection:
          activeSelection?.projectId === projectId &&
          activeSelection.parcelId === parcelId &&
          activeSelection.submissionType === submissionType &&
          (!submissionId || activeSelection.submissionId === submissionId)
            ? null
            : activeSelection,
      });
    },

    clearProjectSelections: (projectId) => {
      const { persistedSelections } = get();
      const updated = { ...persistedSelections };
      // Un choix explicitement vide empêche la sélection automatique d'une équipe au remontage.
      updated[projectId] = {};
      writeStorage(updated);
      set({ persistedSelections: updated, activeSelection: get().activeSelection?.projectId === projectId ? null : get().activeSelection });
    },

    pruneInvalidSelections: (projectId, validSubmissionIds) => {
      const { persistedSelections } = get();
      const projectSelections = persistedSelections[projectId];

      if (!projectSelections) return;

      const updatedProjectSelections: PersistedSelections[string] = {};

      for (const [parcelId, parcelSelections] of Object.entries(
        projectSelections,
      )) {
        const validParcelSelections: Partial<
          Record<SubmissionType, string[]>
        > = {};

        for (const [type, ids] of Object.entries(parcelSelections)) {
          const valid = ids.filter((id) => validSubmissionIds.has(id));
          if (valid.length) validParcelSelections[type as SubmissionType] = valid;
        }

        // Ne garder la parcelle que si elle a des sélections valides
        if (Object.keys(validParcelSelections).length > 0) {
          updatedProjectSelections[parcelId] = validParcelSelections;
        }
      }

      const updatedSelections: PersistedSelections = {
        ...persistedSelections,
        [projectId]: updatedProjectSelections,
      };

      writeStorage(updatedSelections);
      set({ persistedSelections: updatedSelections });
    },
  }),
);

/**
 * Hook utilitaire pour restaurer les sélections après un chargement de projet.
 * À appeler dans useViewerBootstrap après loadProject.
 *
 * @param projectId - ID du projet fraîchement chargé
 * @param projectManager - Référence au ProjectManager pour appliquer les selections
 * @returns Les IDs de soumissions sélectionnées à restaurer
 */
export const getSelectionsToRestore = (
  projectId: string,
): { parcelId: string; type: SubmissionType; submissionId: string }[] => {
  useSelectionPersistenceStore.getState().loadFromStorage();

  const { persistedSelections } = useSelectionPersistenceStore.getState();
  const projectSelections = persistedSelections[projectId];
  if (!projectSelections) return [];

  const selections: {
    parcelId: string;
    type: SubmissionType;
    submissionId: string;
  }[] = [];

  for (const [parcelId, parcelSelections] of Object.entries(
    projectSelections,
  )) {
    for (const [type, ids] of Object.entries(parcelSelections)) {
      for (const submissionId of ids) {
        selections.push({
          parcelId,
          type: type as SubmissionType,
          submissionId,
        });
      }
    }
  }

  return selections;
};
